require('./src/polyfills/object-groupby')
require('./src/assets/css/main.scss')
const React = require('react')
const ReactDOM = require('react-dom/client')
const cookies = require('js-cookie')
const { default: SkeletonElement } = require('./src/components/organisms/Skeleton/Skeleton')
const { AppWrappedProvider } = require('./src/context/AppWrappedContext')
const {
  default: TrustedReviewsBlock,
} = require('./src/components/organisms/TrustedReviews/TrustedReviewsBlock')

const DefaultLayout = require('./src/components/pages/Layout').default
const GlobalNavigation = require('./src/components/organisms/NewNavigation/NewNavigation').default
const GlobalFooter = require('./src/components/organisms/GlobalFooter/NewFooter/NewFooter').default
const RootElement = require('./src/components/root-element').default

const {
  default: RecommendationBlock,
} = require('./src/components/organisms/RecommendationBlock/RecommendationBlock')
const { ROUTES } = require('./src/constants/routes-node')
const {
  CONSENT_COOKIE_NAME,
  getConsentCookieDomain,
  MARKETING_COOKIE_PATTERNS,
  ANALYTICS_COOKIE_PATTERNS,
  MARKETING_STORAGE_PATTERNS,
  ANALYTICS_STORAGE_PATTERNS,
} = require('./src/constants/consent')
const { loadKlaviyo, captureKlaviyoExchangeId } = require('./src/utils/klaviyo/klaviyoOnsite')
const {
  trackPageCategory,
  initResourceDownloadTracking,
} = require('./src/utils/klaviyo/klaviyoEvents')
const GoogleRatingBadge = React.lazy(
  () => import('./src/components/organisms/GoogleRatingBadge/GoogleRatingBadge')
)
const CookieBanner = React.lazy(
  () => import('./src/components/organisms/CookieBanner/CookieBanner')
)

exports.wrapPageElement = ({ element, props }) => {
  const { pathname } = props?.location
  const is404 = !!props?.custom404 || props?.pageResources?.page?.path === '/404.html'
  const showReviewsBlock = !(
    [
      ROUTES.DATA_SECURITY,
      ROUTES.UTILITIES_PAGE,
      ROUTES.FIRST_RESPONDER_PAGE,
      ROUTES.DRONES_SECURITY_PAGE,
      ROUTES.REALITY_CAPTURE_PAGE,
    ].some((_) => pathname.includes(_)) || is404
  )

  return (
    <AppWrappedProvider>
      <GlobalNavigation pageContext={props.pageContext} />
      <DefaultLayout>{element}</DefaultLayout>
      <React.Suspense fallback={<SkeletonElement placeholder="Loading cookie block..." />}>
        <CookieBanner />
      </React.Suspense>
      {/* <GoogleRatingBadge /> */}
      {showReviewsBlock && <TrustedReviewsBlock />}
      <GlobalFooter />
      {/*<RecommendationBlock />*/}
    </AppWrappedProvider>
  )
}

exports.replaceHydrateFunction = () => (element, container) => {
  const root = ReactDOM.createRoot(container)
  root.render(element)
}

// Klaviyo needs analytics consent and loads only after the initial render
let isInitialRenderDone = false
let shouldLoadKlaviyo = false

// Moves a consent cookie saved before it was shared with the checkout to the root domain
const migrateConsentCookie = () => {
  const domain = getConsentCookieDomain()
  const value = cookies.get(CONSENT_COOKIE_NAME)
  if (!domain || !value) {
    return
  }

  cookies.remove(CONSENT_COOKIE_NAME, { path: '/' })
  // Still readable after removing the host-only cookie means it is already on the root domain
  if (!cookies.get(CONSENT_COOKIE_NAME)) {
    cookies.set(CONSENT_COOKIE_NAME, value, {
      domain,
      expires: 365,
      sameSite: 'lax',
      secure: window.location.protocol === 'https:',
    })
  }
}

exports.onClientEntry = () => {
  migrateConsentCookie()
  const consent = cookies.get(CONSENT_COOKIE_NAME)
  captureKlaviyoExchangeId()
  initResourceDownloadTracking()
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag === 'undefined') {
    window.gtag = function gtag() {
      dataLayer.push(arguments)
    }
  }

  // Claydar tracks SPA navigation itself (patches history), so it only needs to load once
  const loadClaydar = () => {
    if (process.env.NODE_ENV !== 'production' || document.getElementById('claydar-init')) {
      return
    }

    const scriptClaydar = document.createElement('script')
    scriptClaydar.id = 'claydar-init'
    scriptClaydar.src = 'https://static.claydar.com/init.v1.js?id=cXfwzpp2zI'
    scriptClaydar.async = true
    // Claydar's install guide asks for the snippet right before </body>
    document.body.appendChild(scriptClaydar)
  }

  const loadMetaPixel = () => {
    if (typeof window.fbq === 'function') {
      return
    }

    const fbq = function fbq() {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments)
      } else {
        fbq.queue.push(arguments)
      }
    }
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    window.fbq = fbq
    window._fbq = window._fbq || fbq

    const scriptMetaPixel = document.createElement('script')
    scriptMetaPixel.src = 'https://connect.facebook.net/en_US/fbevents.js'
    scriptMetaPixel.async = true
    document.head.appendChild(scriptMetaPixel)

    window.fbq('init', '413668329926304')
    window.fbq('track', 'PageView')
  }

  const matchesAny = (patterns, name) => patterns.some((pattern) => pattern.test(name))

  // Removes first-party cookies / storage left by tags whose consent is not granted
  const clearTrackingData = (cookiePatterns, storagePatterns) => {
    const hostParts = window.location.hostname.split('.')
    const domains = [
      undefined,
      ...hostParts.map((_, index) => `.${hostParts.slice(index).join('.')}`).slice(0, -1),
    ]

    Object.keys(cookies.get())
      .filter((name) => matchesAny(cookiePatterns, name))
      .forEach((name) => domains.forEach((domain) => cookies.remove(name, { path: '/', domain })))

    try {
      ;[window.localStorage, window.sessionStorage].forEach((storage) => {
        Object.keys(storage)
          .filter((key) => matchesAny(storagePatterns, key))
          .forEach((key) => storage.removeItem(key))
      })
    } catch (e) {
      console.error(e)
    }
  }

  let currentConsents = null

  window.consentChange = (consent) => {
    const granted = consent?.consents ?? {}
    const isWithdrawn =
      (currentConsents?.ad_storage && !granted.ad_storage) ||
      (currentConsents?.analytics_storage && !granted.analytics_storage)
    currentConsents = granted

    if (granted.ad_storage) {
      loadClaydar()
      loadMetaPixel()
    }

    if (typeof window.oaiq === 'function') {
      window.oaiq('consent', Boolean(granted.ad_storage))
    }

    if (granted.analytics_storage) {
      shouldLoadKlaviyo = true
      if (isInitialRenderDone) {
        loadKlaviyo()
      }
    }

    if (consent) {
      window.gtag('consent', 'update', {
        ad_storage: consent?.consents?.ad_storage ? 'granted' : 'denied',
        analytics_storage: consent?.consents?.analytics_storage ? 'granted' : 'denied',
        ad_user_data: consent?.consents?.ad_user_data ? 'granted' : 'denied',
        ad_personalization: consent?.consents?.ad_personalization ? 'granted' : 'denied',
        personalization_storage: consent?.consents?.personalization_storage ? 'granted' : 'denied',
        functionality_storage: consent?.consents?.functionality_storage ? 'granted' : 'denied',
        security_storage: consent?.consents?.security_storage ? 'granted' : 'denied',
      })
    } else {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        personalization_storage: 'denied',
        functionality_storage: 'denied',
        security_storage: 'granted',
      })
    }

    // GTM trigger for tags with consent checks (LinkedIn, Clarity) so they can fire right after
    // the visitor accepts instead of waiting for the next full page load
    window.dataLayer.push({
      event: 'cookie_consent_update',
      consent_marketing: Boolean(granted.ad_storage),
      consent_analytics: Boolean(granted.analytics_storage),
    })

    if (!granted.ad_storage) {
      clearTrackingData(MARKETING_COOKIE_PATTERNS, MARKETING_STORAGE_PATTERNS)
    }
    if (!granted.analytics_storage) {
      clearTrackingData(ANALYTICS_COOKIE_PATTERNS, ANALYTICS_STORAGE_PATTERNS)
    }

    // Scripts that are already running cannot be unloaded, so a withdrawal reloads the page
    if (isWithdrawn) {
      window.location.reload()
    }
  }

  try {
    const parsedConsent = consent ? JSON.parse(consent) : null
    window.consentChange(parsedConsent)
  } catch (e) {
    console.error(e)
  }
}

exports.wrapRootElement = ({ element }) => <RootElement>{element}</RootElement>

exports.onInitialClientRender = () => {
  isInitialRenderDone = true
  if (shouldLoadKlaviyo) {
    loadKlaviyo()
  }

  // DEPRICATED VARIANT
  //   const scriptGoogleBadge = document.createElement('script')
  //   scriptGoogleBadge.src = 'https://apis.google.com/js/platform.js?onload=renderBadge'
  //   scriptGoogleBadge.async = true
  //   scriptGoogleBadge.defer = true
  //   document.body.appendChild(scriptGoogleBadge)

  //   const raitingGoogleBadgeContainer = document.createElement('script')
  //   raitingGoogleBadgeContainer.innerHTML = `
  //     window.renderBadge = function() {
  //       const ratingBadgeContainer = document.createElement("div");
  //         ratingBadgeContainer.className = 'google-rating-badge-container'
  //         document.body.appendChild(ratingBadgeContainer);
  //         window.gapi.load('ratingbadge', function() {
  //           window.gapi.ratingbadge.render(
  //             ratingBadgeContainer, {
  //               // REQUIRED
  //               "merchant_id": 5476661094,
  //               // OPTIONAL
  //               "position": "BOTTOM_LEFT"
  //             });
  //       });
  //     }
  //   `
  //   document.body.appendChild(raitingGoogleBadgeContainer)
  // }

  // Google Customer Reviews: new store widget (MerchantWidget)
  // Load merchant widget script and start on load per latest docs
  const merchantScript = document.createElement('script')
  merchantScript.id = 'merchantWidgetScript'
  merchantScript.src = 'https://www.gstatic.com/shopping/merchant/merchantwidget.js'
  merchantScript.defer = true
  merchantScript.addEventListener('load', () => {
    if (window.merchantwidget && typeof window.merchantwidget.start === 'function') {
      window.merchantwidget.start({
        position: 'RIGHT_BOTTOM',
        // Optional positioning (defaults per docs)
        sideMargin: 36,
        bottomMargin: 36,
        mobileSideMargin: 16,
        mobileBottomMargin: 46,
      })
    }
  })
  document.head.appendChild(merchantScript)
}

exports.onRouteUpdate = ({ location }) => {
  if (process.env.NODE_ENV !== 'production') {
    return null
  }

  trackPageCategory(location.pathname)

  // The OpenAI pixel does not track page views itself; the title is read after Gatsby's Head updates
  setTimeout(() => {
    if (typeof window.oaiq !== 'function') return

    window.oaiq('measure', 'page_viewed', {
      type: 'contents',
      contents: [
        {
          id: location.pathname,
          name: document.title,
          content_type: 'page',
        },
      ],
    })
  }, 100)

  // const pagePath = location ? location.pathname + location.search + location.hash : undefined
  // setTimeout(() => {
  //   if (typeof window !== 'undefined' && window.dataLayer) {
  //     window.dataLayer.push({ event: 'page_view', page_path: pagePath })
  //   }
  // }, 100)
}

exports.shouldUpdateScroll = ({ prevRouterProps, routerProps }) => {
  const prevPathname = prevRouterProps?.location?.pathname || ''
  const prevSearch = prevRouterProps?.location?.search || ''
  const { pathname, search, hash } = routerProps.location
  const isSamePath = pathname === prevPathname
  const isSignUpFormToggleNavigation =
    isSamePath &&
    (search.includes('open-sign-upform=true') || prevSearch.includes('open-sign-upform=true'))

  const shouldScrollUp = !(
    (search.includes('selectedTab') && isSamePath) ||
    isSignUpFormToggleNavigation
  )

  if (hash) {
    return true
  }

  if (shouldScrollUp) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0)
      })
    })
  }

  return false
}
